import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'
import { CollectionItem } from '../../shared/models/main/collection'
import { BookmarkService } from '../data/bookmark.service'

export interface ApiResponse<T> {
  results: T[]
  count: number
  detail?: string
}

export interface BookmarkGroup {
  id: number
  name: string
  items_count?: number
  words_count?: number
  pairs_count?: number
  dics_count?: number
  notes_count?: number
  quizzes_count?: number
  figures_count?: number
  elems?: string[]
}

interface BookmarkState {
  // All items (collection_id = null)
  allItems: CollectionItem[]
  allItemsPage: number
  allItemsTotal: number
  allItemsEnd: boolean

  // Specific collections
  collections: { [collectionId: number]: CollectionItem[] }
  collectionPages: { [collectionId: number]: number }
  collectionTotals: { [collectionId: number]: number }
  collectionEnds: { [collectionId: number]: boolean }

  // Collection groups (metadata)
  bookmarkGroups: BookmarkGroup[]

  // Loading states
  loading: {
    allItems: boolean
    collections: { [collectionId: number]: boolean }
    bookmarkGroups: boolean
  }

  // Error states
  error: {
    allItems: string | null
    collections: { [collectionId: number]: string | null }
    bookmarkGroups: string | null
  }
}

@Injectable({
  providedIn: 'root',
})
export class BookmarkStateService {
  private readonly ITEMS_PER_PAGE = 10

  private state: BookmarkState = {
    allItems: [],
    allItemsPage: 1,
    allItemsTotal: 0,
    allItemsEnd: false,
    collections: {},
    collectionPages: {},
    collectionTotals: {},
    collectionEnds: {},
    bookmarkGroups: [],
    loading: {
      allItems: false,
      collections: {},
      bookmarkGroups: false,
    },
    error: {
      allItems: null,
      collections: {},
      bookmarkGroups: null,
    },
  }

  private stateSubject = new BehaviorSubject<BookmarkState>(this.state)

  constructor(private bookmarkService: BookmarkService) {}

  // Selectors
  get allItems$(): Observable<CollectionItem[]> {
    return this.stateSubject.pipe(map(state => state.allItems))
  }

  get allItemsTotal$(): Observable<number> {
    return this.stateSubject.pipe(map(state => state.allItemsTotal))
  }

  get allItemsEnd$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.allItemsEnd))
  }

  get allItemsLoading$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.allItems))
  }

  get allItemsError$(): Observable<string | null> {
    return this.stateSubject.pipe(map(state => state.error.allItems))
  }

  get bookmarkGroups$(): Observable<BookmarkGroup[]> {
    return this.stateSubject.pipe(map(state => state.bookmarkGroups))
  }

  get bookmarkGroupsLoading$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.bookmarkGroups))
  }

  get bookmarkGroupsError$(): Observable<string | null> {
    return this.stateSubject.pipe(map(state => state.error.bookmarkGroups))
  }

  // Collection-specific selectors
  getCollectionItems$(collectionId: number): Observable<CollectionItem[]> {
    return this.stateSubject.pipe(map(state => state.collections[collectionId] || []))
  }

  getCollectionTotal$(collectionId: number): Observable<number> {
    return this.stateSubject.pipe(map(state => state.collectionTotals[collectionId] || 0))
  }

  getCollectionEnd$(collectionId: number): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.collectionEnds[collectionId] || false))
  }

  getCollectionLoading$(collectionId: number): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.collections[collectionId] || false))
  }

  getCollectionError$(collectionId: number): Observable<string | null> {
    return this.stateSubject.pipe(map(state => state.error.collections[collectionId] || null))
  }

  // Get current state values synchronously
  get currentAllItems(): CollectionItem[] {
    return this.state.allItems
  }

  getCurrentCollectionItems(collectionId: number): CollectionItem[] {
    return this.state.collections[collectionId] || []
  }

  get currentBookmarkGroups(): BookmarkGroup[] {
    return this.state.bookmarkGroups
  }

  // Actions

  // Load all items (bookmarks across all collections)
  async loadAllItems(reset: boolean = false): Promise<void> {
    if (reset) {
      this.updateState({
        ...this.state,
        allItems: [],
        allItemsPage: 1,
        allItemsEnd: false,
      })
    }

    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, allItems: true },
      error: { ...this.state.error, allItems: null },
    })

    try {
      const response = (await firstValueFrom(
        this.bookmarkService.get_collection_items_page(
          this.state.allItemsPage,
          this.ITEMS_PER_PAGE,
          null, // null for all items
        ),
      )) as ApiResponse<CollectionItem>

      if (response && response.results) {
        this.updateState({
          ...this.state,
          allItems: reset ? response.results : [...this.state.allItems, ...response.results],
          allItemsTotal: response.count || 0,
          allItemsEnd:
            response.detail === 'Invalid page.' || response.results.length < this.ITEMS_PER_PAGE,
          loading: { ...this.state.loading, allItems: false },
        })
      } else {
        this.updateState({
          ...this.state,
          loading: { ...this.state.loading, allItems: false },
          error: { ...this.state.error, allItems: 'No items found' },
        })
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, allItems: false },
        error: {
          ...this.state.error,
          allItems: error instanceof Error ? error.message : 'Failed to load items',
        },
      })
      throw error
    }
  }

  // Load items for a specific collection
  async loadCollectionItems(collectionId: number, reset: boolean = false): Promise<void> {
    if (reset) {
      this.updateState({
        ...this.state,
        collections: { ...this.state.collections, [collectionId]: [] },
        collectionPages: { ...this.state.collectionPages, [collectionId]: 1 },
        collectionEnds: { ...this.state.collectionEnds, [collectionId]: false },
      })
    }

    this.updateState({
      ...this.state,
      loading: {
        ...this.state.loading,
        collections: { ...this.state.loading.collections, [collectionId]: true },
      },
      error: {
        ...this.state.error,
        collections: { ...this.state.error.collections, [collectionId]: null },
      },
    })

    try {
      const currentPage = this.state.collectionPages[collectionId] || 1
      const response = (await firstValueFrom(
        this.bookmarkService.get_collection_items_page(
          currentPage,
          this.ITEMS_PER_PAGE,
          collectionId,
        ),
      )) as ApiResponse<CollectionItem>

      if (response && response.results) {
        const currentItems = this.state.collections[collectionId] || []
        this.updateState({
          ...this.state,
          collections: {
            ...this.state.collections,
            [collectionId]: reset ? response.results : [...currentItems, ...response.results],
          },
          collectionTotals: { ...this.state.collectionTotals, [collectionId]: response.count || 0 },
          collectionEnds: {
            ...this.state.collectionEnds,
            [collectionId]:
              response.detail === 'Invalid page.' || response.results.length < this.ITEMS_PER_PAGE,
          },
          loading: {
            ...this.state.loading,
            collections: { ...this.state.loading.collections, [collectionId]: false },
          },
        })
      } else {
        this.updateState({
          ...this.state,
          loading: {
            ...this.state.loading,
            collections: { ...this.state.loading.collections, [collectionId]: false },
          },
          error: {
            ...this.state.error,
            collections: { ...this.state.error.collections, [collectionId]: 'No items found' },
          },
        })
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: {
          ...this.state.loading,
          collections: { ...this.state.loading.collections, [collectionId]: false },
        },
        error: {
          ...this.state.error,
          collections: {
            ...this.state.error.collections,
            [collectionId]:
              error instanceof Error ? error.message : 'Failed to load collection items',
          },
        },
      })
      throw error
    }
  }

  // Load bookmark groups (collections metadata)
  async loadBookmarkGroups(): Promise<void> {
    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, bookmarkGroups: true },
      error: { ...this.state.error, bookmarkGroups: null },
    })

    try {
      const response = (await firstValueFrom(this.bookmarkService.get_bookmark_groups())) as any

      if (response && !response.detail && Array.isArray(response)) {
        // Ensure all bookmark groups have required properties with defaults
        const normalizedBookmarkGroups: BookmarkGroup[] = response.map((group: any) => ({
          id: group.id,
          name: group.name,
          items_count: group.items_count || 0,
          words_count: group.words_count || 0,
          pairs_count: group.pairs_count || 0,
          dics_count: group.dics_count || 0,
          notes_count: group.notes_count || 0,
          quizzes_count: group.quizzes_count || 0,
          figures_count: group.figures_count || 0,
          elems: group.elems || [],
        }))

        this.updateState({
          ...this.state,
          bookmarkGroups: normalizedBookmarkGroups,
          loading: { ...this.state.loading, bookmarkGroups: false },
        })
      } else {
        this.updateState({
          ...this.state,
          loading: { ...this.state.loading, bookmarkGroups: false },
          error: { ...this.state.error, bookmarkGroups: 'No bookmark groups found' },
        })
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, bookmarkGroups: false },
        error: {
          ...this.state.error,
          bookmarkGroups: error instanceof Error ? error.message : 'Failed to load bookmark groups',
        },
      })
      throw error
    }
  }

  // Load more items for all items
  async loadMoreAllItems(): Promise<void> {
    if (!this.state.loading.allItems && !this.state.allItemsEnd) {
      this.updateState({
        ...this.state,
        allItemsPage: this.state.allItemsPage + 1,
      })
      return this.loadAllItems()
    }
  }

  // Load more items for a specific collection
  async loadMoreCollectionItems(collectionId: number): Promise<void> {
    const isLoading = this.state.loading.collections[collectionId]
    const isEnd = this.state.collectionEnds[collectionId]

    if (!isLoading && !isEnd) {
      const currentPage = this.state.collectionPages[collectionId] || 1
      this.updateState({
        ...this.state,
        collectionPages: { ...this.state.collectionPages, [collectionId]: currentPage + 1 },
      })
      return this.loadCollectionItems(collectionId)
    }
  }

  // Add item to bookmark state
  addBookmarkItem(item: CollectionItem, collectionId?: number): void {
    // Add to all items if it's not already there
    const allItemsExists = this.state.allItems.some(
      existingItem => existingItem.id === item.id && existingItem.type === item.type,
    )

    if (!allItemsExists) {
      this.updateState({
        ...this.state,
        allItems: [item, ...this.state.allItems],
        allItemsTotal: this.state.allItemsTotal + 1,
      })
    }

    // Add to specific collection if specified
    if (collectionId !== undefined) {
      const collectionItems = this.state.collections[collectionId] || []
      const collectionItemExists = collectionItems.some(
        existingItem => existingItem.id === item.id && existingItem.type === item.type,
      )

      if (!collectionItemExists) {
        this.updateState({
          ...this.state,
          collections: {
            ...this.state.collections,
            [collectionId]: [item, ...collectionItems],
          },
          collectionTotals: {
            ...this.state.collectionTotals,
            [collectionId]: (this.state.collectionTotals[collectionId] || 0) + 1,
          },
        })
      }
    }
  }

  // Update existing item in bookmark state
  updateBookmarkItem(item: CollectionItem): void {
    // Update in all items
    const updatedAllItems = this.state.allItems.map(existingItem =>
      existingItem.id === item.id && existingItem.type === item.type ? item : existingItem,
    )

    // Update in all collections
    const updatedCollections = { ...this.state.collections }
    Object.keys(updatedCollections).forEach(collectionId => {
      updatedCollections[parseInt(collectionId)] = updatedCollections[parseInt(collectionId)].map(
        existingItem =>
          existingItem.id === item.id && existingItem.type === item.type ? item : existingItem,
      )
    })

    this.updateState({
      ...this.state,
      allItems: updatedAllItems,
      collections: updatedCollections,
    })
  }

  // Remove item from bookmark state
  removeBookmarkItem(itemId: number, itemType: string, collectionId?: number): void {
    // Remove from all items
    const filteredAllItems = this.state.allItems.filter(
      item => !(item.id === itemId && item.type === itemType),
    )

    let updatedState = {
      ...this.state,
      allItems: filteredAllItems,
      allItemsTotal: Math.max(0, this.state.allItemsTotal - 1),
    }

    // Remove from specific collection if specified
    if (collectionId !== undefined) {
      const collectionItems = this.state.collections[collectionId] || []
      const filteredCollectionItems = collectionItems.filter(
        item => !(item.id === itemId && item.type === itemType),
      )

      updatedState = {
        ...updatedState,
        collections: {
          ...this.state.collections,
          [collectionId]: filteredCollectionItems,
        },
        collectionTotals: {
          ...this.state.collectionTotals,
          [collectionId]: Math.max(0, (this.state.collectionTotals[collectionId] || 0) - 1),
        },
      }
    } else {
      // If no specific collection, remove from all collections
      const updatedCollections = { ...this.state.collections }
      const updatedCollectionTotals = { ...this.state.collectionTotals }

      Object.keys(updatedCollections).forEach(collectionIdKey => {
        const collectionIdNum = parseInt(collectionIdKey)
        const filteredItems = updatedCollections[collectionIdNum].filter(
          item => !(item.id === itemId && item.type === itemType),
        )

        if (filteredItems.length !== updatedCollections[collectionIdNum].length) {
          updatedCollections[collectionIdNum] = filteredItems
          updatedCollectionTotals[collectionIdNum] = Math.max(
            0,
            (updatedCollectionTotals[collectionIdNum] || 0) - 1,
          )
        }
      })

      updatedState = {
        ...updatedState,
        collections: updatedCollections,
        collectionTotals: updatedCollectionTotals,
      }
    }

    this.updateState(updatedState)
  }

  // Add new bookmark group
  addBookmarkGroup(group: BookmarkGroup): void {
    this.updateState({
      ...this.state,
      bookmarkGroups: [group, ...this.state.bookmarkGroups],
    })
  }

  // Create new bookmark group via API
  async createBookmarkGroup(name: string): Promise<BookmarkGroup> {
    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, bookmarkGroups: true },
      error: { ...this.state.error, bookmarkGroups: null },
    })

    try {
      const response = (await firstValueFrom(this.bookmarkService.add_bookmark_group(name))) as any

      if (response && response.name && response.id && response.success !== false) {
        // Success case - create bookmark group object
        const newBookmarkGroup: BookmarkGroup = {
          id: response.id,
          name: response.name,
          items_count: response.items_count || 0,
          words_count: response.words_count || 0,
          pairs_count: response.pairs_count || 0,
          dics_count: response.dics_count || 0,
          elems: response.elems || [],
        }

        // Add to state
        this.updateState({
          ...this.state,
          bookmarkGroups: [newBookmarkGroup, ...this.state.bookmarkGroups],
          loading: { ...this.state.loading, bookmarkGroups: false },
        })
        return newBookmarkGroup
      } else {
        // Error case - handle failed response
        const errorMessage =
          response?.res || response?.detail || response?.error || 'Failed to create collection'
        console.error('BookmarkStateService: Failed to create bookmark group:', errorMessage)
        this.updateState({
          ...this.state,
          loading: { ...this.state.loading, bookmarkGroups: false },
          error: { ...this.state.error, bookmarkGroups: errorMessage },
        })
        throw new Error(errorMessage)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create collection'
      console.error('BookmarkStateService: Error creating bookmark group:', error)
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, bookmarkGroups: false },
        error: { ...this.state.error, bookmarkGroups: errorMessage },
      })
      throw error
    }
  }

  // Update existing bookmark group
  updateBookmarkGroup(group: BookmarkGroup): void {
    const updatedGroups = this.state.bookmarkGroups.map(existingGroup =>
      existingGroup.id === group.id ? group : existingGroup,
    )

    this.updateState({
      ...this.state,
      bookmarkGroups: updatedGroups,
    })
  }

  // Remove bookmark group
  removeBookmarkGroup(groupId: number): void {
    const filteredGroups = this.state.bookmarkGroups.filter(group => group.id !== groupId)

    // Also clean up the collections data for this group
    const updatedCollections = { ...this.state.collections }
    const updatedCollectionPages = { ...this.state.collectionPages }
    const updatedCollectionTotals = { ...this.state.collectionTotals }
    const updatedCollectionEnds = { ...this.state.collectionEnds }
    const updatedLoadingCollections = { ...this.state.loading.collections }
    const updatedErrorCollections = { ...this.state.error.collections }

    delete updatedCollections[groupId]
    delete updatedCollectionPages[groupId]
    delete updatedCollectionTotals[groupId]
    delete updatedCollectionEnds[groupId]
    delete updatedLoadingCollections[groupId]
    delete updatedErrorCollections[groupId]

    this.updateState({
      ...this.state,
      bookmarkGroups: filteredGroups,
      collections: updatedCollections,
      collectionPages: updatedCollectionPages,
      collectionTotals: updatedCollectionTotals,
      collectionEnds: updatedCollectionEnds,
      loading: {
        ...this.state.loading,
        collections: updatedLoadingCollections,
      },
      error: {
        ...this.state.error,
        collections: updatedErrorCollections,
      },
    })
  }

  // Clear all state
  clearState(): void {
    this.state = {
      allItems: [],
      allItemsPage: 1,
      allItemsTotal: 0,
      allItemsEnd: false,
      collections: {},
      collectionPages: {},
      collectionTotals: {},
      collectionEnds: {},
      bookmarkGroups: [],
      loading: {
        allItems: false,
        collections: {},
        bookmarkGroups: false,
      },
      error: {
        allItems: null,
        collections: {},
        bookmarkGroups: null,
      },
    }
    this.stateSubject.next(this.state)
  }

  // Clear specific collection state
  clearCollectionState(collectionId: number): void {
    const updatedCollections = { ...this.state.collections }
    const updatedCollectionPages = { ...this.state.collectionPages }
    const updatedCollectionTotals = { ...this.state.collectionTotals }
    const updatedCollectionEnds = { ...this.state.collectionEnds }
    const updatedLoadingCollections = { ...this.state.loading.collections }
    const updatedErrorCollections = { ...this.state.error.collections }

    delete updatedCollections[collectionId]
    delete updatedCollectionPages[collectionId]
    delete updatedCollectionTotals[collectionId]
    delete updatedCollectionEnds[collectionId]
    delete updatedLoadingCollections[collectionId]
    delete updatedErrorCollections[collectionId]

    this.updateState({
      ...this.state,
      collections: updatedCollections,
      collectionPages: updatedCollectionPages,
      collectionTotals: updatedCollectionTotals,
      collectionEnds: updatedCollectionEnds,
      loading: {
        ...this.state.loading,
        collections: updatedLoadingCollections,
      },
      error: {
        ...this.state.error,
        collections: updatedErrorCollections,
      },
    })
  }

  private updateState(newState: BookmarkState): void {
    this.state = newState
    this.stateSubject.next(this.state)
  }
}
